import { ResumeDto, UpdateResumeDto } from "@reactive-resume/dto";
import { useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";

import { track } from "@/client/analytics/mixpanel";
import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";
import { useSaveStatusStore } from "@/client/stores/save-status";

// Simple throttle implementation to avoid adding dependency
function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  options: { leading?: boolean; trailing?: boolean } = {},
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastExecuted = 0;

  return function (this: any, ...args: Parameters<T>) {
    const now = Date.now();
    const elapsed = now - lastExecuted;

    const execute = () => {
      lastExecuted = now;
      func.apply(this, args);
    };

    if (elapsed >= wait) {
      if (options.leading !== false) {
        execute();
      }
    } else if (options.trailing !== false && !timeout) {
      timeout = setTimeout(() => {
        timeout = null;
        if (options.trailing !== false) {
          execute();
        }
      }, wait - elapsed);
    }
  };
}

// Track resume edits with throttling to avoid excessive events
const trackResumeEdit = throttle(
  (resumeId: string) => {
    track("resume_edited", { resume_id: resumeId, edit_type: "manual" });
  },
  300000, // Track at most once every 5 minutes during active editing
  { leading: true, trailing: false },
);

export const updateResume = async (data: UpdateResumeDto) => {
  const response = await axios.patch<ResumeDto, AxiosResponse<ResumeDto>, UpdateResumeDto>(
    `/resume/${data.id}`,
    data,
  );

  queryClient.setQueryData<ResumeDto>(["resume", { id: response.data.id }], response.data);

  queryClient.setQueryData<ResumeDto[]>(["resumes"], (cache) => {
    if (!cache) return [response.data];
    return cache.map((resume) => {
      if (resume.id === response.data.id) return response.data;
      return resume;
    });
  });

  // Track edit (throttled to once per 5 minutes)
  trackResumeEdit(response.data.id);

  return response.data;
};

/**
 * Autosave pipeline.
 *
 * Previously this was a bare `debounce(updateResume, 500)`: nothing awaited the returned
 * promise, so a failed save was swallowed as an unhandled rejection and the user kept typing
 * into a document that had silently stopped persisting. We now track the pending payload,
 * surface every state transition to `useSaveStatusStore`, and expose a flush/retry so the
 * builder can guard navigation and offer recovery.
 */
let pendingPayload: UpdateResumeDto | null = null;
let inFlight = false;

// Bumped on reset. A request that was already in flight when the builder unmounted must not
// write its result into the status of whatever resume is opened next.
let generation = 0;

const runSave = async () => {
  if (!pendingPayload || inFlight) return;

  const payload = pendingPayload;
  const currentGeneration = generation;
  pendingPayload = null;
  inFlight = true;
  useSaveStatusStore.getState().setStatus("saving");

  try {
    await updateResume(payload);
    if (currentGeneration !== generation) return;

    // An edit that landed while this request was in flight leaves a newer payload queued —
    // don't claim "Saved" until that one lands too.
    if (pendingPayload) useSaveStatusStore.getState().setStatus("saving");
    else useSaveStatusStore.getState().setSaved();
  } catch {
    if (currentGeneration !== generation) return;

    // Keep the newest payload so a retry resends current data, not this stale snapshot.
    pendingPayload = pendingPayload ?? payload;
    useSaveStatusStore.getState().setStatus("error");
  } finally {
    // Always clear, even across a reset — otherwise a stale in-flight flag would block every
    // future save for the next resume.
    inFlight = false;
    if (currentGeneration === generation && pendingPayload) void debouncedRunSave();
  }
};

// Tracked so `flushResumeUpdate` can await the actual network round-trip. Callers such as the
// ATS check rely on the resume being persisted before they run their analysis.
let inFlightPromise: Promise<void> | null = null;

const startSave = () => {
  inFlightPromise = runSave();
  return inFlightPromise;
};

const debouncedRunSave = debounce(startSave, 500);

export const debouncedUpdateResume = (data: UpdateResumeDto) => {
  pendingPayload = data;
  useSaveStatusStore.getState().setStatus("saving");
  void debouncedRunSave();
};

/** True while edits exist only in memory — used to guard tab close and navigation. */
export const hasUnsavedResumeChanges = () => pendingPayload !== null || inFlight;

/**
 * Send the queued edit immediately instead of waiting out the debounce (⌘S, unmount, ATS run)
 * and resolve once it has actually landed. Drains, because an edit arriving mid-flight queues
 * another round; the iteration cap keeps a pathological edit loop from hanging the caller.
 */
export const flushResumeUpdate = async () => {
  for (let i = 0; i < 5 && (pendingPayload !== null || inFlight); i++) {
    debouncedRunSave.flush();
    await inFlightPromise;
  }
};

/** Re-attempt a failed save from the "Couldn't save" affordance in the builder header. */
export const retryResumeUpdate = async () => {
  debouncedRunSave.cancel();
  await startSave();
};

/** Drop queued work when switching resumes so one document can't save over another. */
export const resetResumeUpdateQueue = () => {
  debouncedRunSave.cancel();
  generation += 1;
  pendingPayload = null;
  useSaveStatusStore.getState().reset();
};

export const useUpdateResume = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: updateResumeFn,
  } = useMutation({
    mutationFn: updateResume,
  });

  return { updateResume: updateResumeFn, loading, error };
};
